'use es6';

import { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { bindAll } from 'underscore';
import shallowCompare from 'react-addons-shallow-compare';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UIButton from 'UIComponents/button/UIButton';
import UIList from 'UIComponents/list/UIList';
import withVisibilityChecker from 'simple-menus-lib/components/menu/decorators/withVisibilityChecker';
import withPromotingMenuDropTargets from 'simple-menus-lib/components/menu/decorators/withPromotingMenuDropTargets';
import withConnectedMenuNodeDivider from 'simple-menus-lib/components/menu/decorators/withConnectedMenuNodeDivider';
import menuDividerDropTarget from 'simple-menus-lib/components/dnd/decorators/menuDividerDropTarget';
import MenuNodeTypes from 'simple-menus-core/enums/MenuNodeTypes';
import { MENU_TREE_INDENTATION, MENU_DIVIDER_MARGIN_OFFSET } from 'simple-menus-core/constants/constants';
import UsageTracker from 'simple-menus-lib/utils/usageTracker/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `simpleMenusLib.components.menuItemDivider.addItems.${key}`;
const actions = [MenuNodeTypes.NO_LINK, MenuNodeTypes.PAGE_LINK, MenuNodeTypes.PAGE_LINK_WITH_PARAMS, MenuNodeTypes.URL_LINK];
class MenuNodeDivider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      isHovering: false
    };
    bindAll(this, ['handleMouseClick', 'onOpenChange', 'handleNodeCreate', 'toggleOpen', 'handleScroll', 'onMouseEnter', 'onMouseLeave']);
  }
  componentDidMount() {
    document.addEventListener('click', this.handleMouseClick);
    document.addEventListener('wheel', this.handleScroll);
  }
  shouldComponentUpdate(nextProps, nextState) {
    if (!nextProps.isVisible) return false;
    const compareResult = shallowCompare(this, nextProps, nextState);
    if (nextProps.isOver !== this.props.isOver || nextState.isHovering !== this.state.isHovering || nextState.isOpen !== this.state.isOpen) {
      return compareResult;
    }
    if (!(nextState.isHovering || nextProps.isOver)) return false;
    return compareResult;
  }
  componentWillUnmount() {
    document.removeEventListener('click', this.handleMouseClick);
    document.removeEventListener('wheel', this.handleScroll);
  }
  get classNames() {
    return classNames('simple-menus-menu-node-divider', this.state.isOpen && 'is--open', this.props.isOver && 'is--over', this.props.readOnly && 'is--readOnly');
  }
  get dividerClassNames() {
    return 'divider' + (this.props.isOver && !this.props.canDrop ? " no--drop" : "");
  }
  get bodyStyles() {
    const {
      depth,
      depthToNextSibling,
      isDragging,
      hasChildren,
      isLastChild,
      depth: isNested
    } = this.props;
    const hasDropTargets = !(hasChildren || !isLastChild || !isNested || !isDragging);
    if (isDragging) {
      if (!hasDropTargets) {
        return {
          width: '100%',
          marginLeft: '0'
        };
      }
      return {
        width: `calc(100% + ${depthToNextSibling * MENU_TREE_INDENTATION}px)`,
        marginLeft: `-${depthToNextSibling * MENU_TREE_INDENTATION}px`
      };
    }
    return {
      width: `calc(100% + ${(depth + MENU_DIVIDER_MARGIN_OFFSET) * MENU_TREE_INDENTATION}px)`,
      marginLeft: `-${(depth + MENU_DIVIDER_MARGIN_OFFSET) * MENU_TREE_INDENTATION}px`
    };
  }
  handleMouseClick({
    target
  }) {
    if (this.state.isOpen) {
      const domNode = findDOMNode(this);
      if (!domNode || !domNode.contains(target)) {
        this.setState({
          isOpen: false
        });
      }
    }
  }
  handleScroll() {
    if (this.state.isOpen) {
      this.setState({
        isOpen: false
      });
    }
  }
  onOpenChange({
    target: {
      value
    }
  }) {
    this.setState({
      isOpen: value
    });
  }
  toggleOpen() {
    this.setState({
      isOpen: !this.state.isOpen
    });
  }
  handleNodeCreate(type) {
    this.props.addNode(type);
    UsageTracker.track('addMenuItem', {
      type,
      method: 'divider'
    });
    this.setState({
      isOpen: false
    });
  }
  onMouseEnter() {
    if (this.props.readOnly) return;
    this.setState({
      isHovering: true
    });
  }
  onMouseLeave() {
    this.setState({
      isHovering: false
    });
  }
  renderBodyContent() {
    const handlers = {
      [MenuNodeTypes.NO_LINK]: () => this.handleNodeCreate(MenuNodeTypes.NO_LINK),
      [MenuNodeTypes.PAGE_LINK]: () => this.handleNodeCreate(MenuNodeTypes.PAGE_LINK),
      [MenuNodeTypes.PAGE_LINK_WITH_PARAMS]: () => {
        this.handleNodeCreate(MenuNodeTypes.PAGE_LINK_WITH_PARAMS);
      },
      [MenuNodeTypes.URL_LINK]: () => this.handleNodeCreate(MenuNodeTypes.URL_LINK)
    };
    const buttons = actions.map(action => {
      return /*#__PURE__*/_jsx(UIButton, {
        onClick: handlers[action],
        use: "unstyled",
        className: "simple-menus-menu-node-divider-option-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(action)
        })
      }, action);
    });
    return /*#__PURE__*/_jsx(UIList, {
      className: "p-y-2",
      children: buttons
    });
  }
  renderPopover() {
    const {
      isHovering,
      isOpen
    } = this.state;
    const {
      isOver,
      isParentDragging,
      isDragging
    } = this.props;
    if (!isOpen && (isOver || isParentDragging || isDragging || !isHovering)) return null;
    return /*#__PURE__*/_jsx(UIPopover, {
      open: isOpen,
      onOpenChange: this.onOpenChange,
      placement: "bottom right",
      content: this.renderBodyContent(),
      closeOnOutsideClick: false,
      children: /*#__PURE__*/_jsx(UIIconButton, {
        size: "extra-small",
        className: "add-button",
        onClick: this.toggleOpen,
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "add"
        })
      })
    });
  }
  render() {
    const {
      connectDropTarget,
      dropTargets
    } = this.props;
    return connectDropTarget( /*#__PURE__*/_jsxs("div", {
      className: this.classNames,
      style: this.bodyStyles,
      onClick: this.toggleOpen,
      onMouseEnter: this.onMouseEnter,
      onMouseLeave: this.onMouseLeave,
      children: [dropTargets, this.renderPopover(), /*#__PURE__*/_jsx("div", {
        className: this.dividerClassNames
      })]
    }));
  }
}
MenuNodeDivider.propTypes = {
  depth: PropTypes.number.isRequired,
  addNode: PropTypes.func.isRequired,
  isParentDragging: PropTypes.bool,
  connectDropTarget: PropTypes.func.isRequired,
  isOver: PropTypes.bool,
  canDrop: PropTypes.bool,
  dropTargets: PropTypes.node,
  depthToNextSibling: PropTypes.number,
  isDragging: PropTypes.bool,
  isLastChild: PropTypes.bool,
  hasChildren: PropTypes.bool,
  isVisible: PropTypes.bool,
  readOnly: PropTypes.bool
};
export default compose(withConnectedMenuNodeDivider(), withPromotingMenuDropTargets(), menuDividerDropTarget(), withVisibilityChecker())(MenuNodeDivider);